import {
  GraphQueryMgr,
  AutoCompleteQueryReq,
  AutoCompleteQueryResp,
  ResourceDetails,
  AutoCompleteRespTypeEnum
} from '@src/endpoints/services/graphQueryMgr.service'
import { map, isEqual } from 'lodash'
import { View } from './graph.helpers'
import type { State } from '@src/redux/configureStore'
import type { SearchResultTableRow } from '@src/endpoints/services/commonHdr.service'
import type { AccountInfo } from '@src/endpoints/services/base.service'
import type { SearchResult } from '@src/redux/reducers/autocomplete.query'
import type { APIResponse } from '@src/endpoints'
import type { Graph } from '@src/components/vis.js.network.vis/models'
import type { Policies } from './policy.utils'
import {
  parsedItemByAccountInfo,
  parseCommonResourceFields
} from './resource.utils'
import { defaultHandleStatus } from '@src/endpoints/helpers'

export const makeActionTypeMaker = (prefix: string) => (actionName: string) =>
  prefix + '/' + actionName.toUpperCase()

export const isEqualGraphs = (oldGraph: Graph, newGraph: Graph) =>
  isEqual(oldGraph, newGraph)

interface GetGraphOptions<State> {
  state: State
  graph: Graph
  stat?: keyof State | 'graph'
  pendingStat?: keyof State | 'isPendingGraph'
}

interface GetGraphOptionsResponse {
  [k: string]: boolean | Graph
  graph: Graph
}

export const getGraph = <State>({
  graph,
  state,
  stat = 'graph',
  pendingStat = 'isPendingGraph'
}: GetGraphOptions<State>): GetGraphOptionsResponse => {
  const hasDataGraph = graph && graph.nodes && graph.links && graph.paths
  return state && isEqual(state[stat as keyof State] as Graph, graph)
    ? { graph: state[stat as keyof State], [pendingStat]: false }
    : {
        graph,
        [pendingStat]: hasDataGraph ? state[pendingStat as keyof State] : false
      }
}

interface GetAutoGenerateInputOptopns {
  queryBy?: string
  accountId?: string
  withAccess?: string
  selectedGraphView?: View
  usageBy?: number
}

export const getAutoGenerateInput = ({
  usageBy,
  selectedGraphView,
  queryBy = 'network_node or public_node',
  withAccess = 'ANY'
}: GetAutoGenerateInputOptopns) => {
  const subQueries = []
  const afterAccess = []
  const usage = getUsageStr(usageBy)
  //if (accountId) {
  //  subQueries.push(`with-attribute accountID ${accountId}`)
  //}
  if (selectedGraphView === View.privilege) {
    subQueries.push('with-privilege admin')
  }
  if (selectedGraphView === View.crossAccount) {
    afterAccess.push('x-account')
  }
  const subQueriesString = subQueries.join(' ')
  const afterAccessString = afterAccess.join(' ')
  const query = `${queryBy} ${subQueriesString} with-access ${withAccess} ${afterAccessString} ${usage}\n`
  //remove extra spaces
  return query.replace(/  +/g, ' ')
}

export interface AutocompleteSearchOptions {
  payload?: AutoCompleteQueryReq
  actionBefore?: (data?: SearchResultTableRow) => void
  actionSuccess?: (
    graph?: Graph,
    table?: SearchResult[] | AutocompleteTable[],
    objects?: ResourceDetails[],
    identities?: ResourceDetails[],
    policies?: ResourceDetails[],
    autoCompleteRespType?: AutoCompleteRespTypeEnum,
    sqlQueryUserUsage?: string
  ) => void
  actionError?: (data?: string) => void
  actionPending?: (data?: boolean) => void
  showToastMessage?: boolean
  handleStatus?: (
    requestResult: APIResponse<AutoCompleteQueryResp>,
    actionError?: (data?: string) => void,
    showToastMessage?: boolean,
    dispatch?: (data) => void
  ) => Promise<APIResponse<AutoCompleteQueryResp>>
  preformatTable?: (data?: SearchResultTableRow[]) => SearchResult[]
  callback?: () => void
}

export function autocompleteSearch({
  payload,
  actionBefore,
  actionPending,
  actionError,
  actionSuccess,
  showToastMessage = false,
  preformatTable,
  callback,
  handleStatus = defaultHandleStatus
}: AutocompleteSearchOptions) {
  return (dispatch: any, getStore: () => State) => {
    const { auth } = getStore()
    const { tenant } = auth
    const { account_info = [] } = tenant || {}

    if (actionBefore) {
      dispatch(actionBefore())
    }

    if (actionPending) {
      dispatch(actionPending(true))
    }
    return GraphQueryMgr.AutoCompleteQuery({ params: payload })
      .then(
        result =>
          handleStatus &&
          handleStatus(result, actionError, showToastMessage, dispatch)
      )
      .then((res: AutoCompleteQueryResp) => {
        const graph: Graph = res.result_graph
          ? JSON.parse(res.result_graph)
          : {}
        let formattedTable: SearchResult[] | AutocompleteTable[] = []
        if (preformatTable) {
          formattedTable = preformatTable(res.result_table || [])
        } else {
          formattedTable = formatAutocompleteTable(
            res.result_table || [],
            account_info
          )
        }
        if (actionSuccess) {
          dispatch(
            actionSuccess(
              graph,
              formattedTable,
              res.objects,
              res.identities,
              res.policies
            )
          )
        }
        if (actionPending) {
          dispatch(actionPending(false))
        }
        if (callback) {
          callback()
        }
      })
      .catch((res: Response) => dispatch(actionError(res?.statusText)))
  }
}

export type AutocompleteTable = Omit<SearchResult, 'policies'> & {
  policies?: Policies[]
}

export function formatAutocompleteTable(
  arr: SearchResultTableRow[],
  account_info?: AccountInfo[]
): AutocompleteTable[] {
  return map<SearchResultTableRow, AutocompleteTable>(arr, item => {
    const newItem: AutocompleteTable = {
      ...item,
      ...parseCommonResourceFields([item]),
      ...parsedItemByAccountInfo(item, account_info)
    }
    return newItem
  })
}

export function getUsageStr(usageBy?: number) {
  let usage = ''
  if (usageBy === 0) {
    usage = ` used-in > 90`
  } else if (usageBy && usageBy > 0) {
    usage = ` used-in < ${usageBy}`
  }
  return usage
}
